import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { API_URL } from "../../config";
import axios from "axios";
import CustomerHeader from "./crmHeader";
import "./css/Dashboard.css";

const RECENT_SALES_VISIT_APIS = [
    `${API_URL}/api/get-sales-customer-visit-reports`,
    `${API_URL}/api/get-recent-sales-visits`,
    `${API_URL}/get-recent-sales-visits`,
];
const SALES_CUSTOMER_APIS = [
    `${API_URL}/get-sales-customers`,
    `${API_URL}/api/get-sales-customers`,
];
const SALES_VISIT_SUMMARY_APIS = [
    `${API_URL}/api/get-sales-visit-summary`,
    `${API_URL}/get-sales-visit-summary`,
];
const SALES_CUSTOMER_SUMMARY_APIS = [
    `${API_URL}/api/get-sales-customer-summary`,
    `${API_URL}/get-sales-customer-summary`,
];
const QUOTATION_APIS = [
    `${API_URL}/api/get-quotations`,
    `${API_URL}/get-quotations`,
];
const SALES_QUOTATION_SUMMARY_APIS = [
    `${API_URL}/api/get-sales-quotation-summary`,
    `${API_URL}/get-sales-quotation-summary`,
    `${API_URL}/api/api/get-sales-quotation-summary`,
];

function CrmDashboard({ authInfo }) {
    const [recentVisits, setRecentVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [totalQuotations, setTotalQuotations] = useState(0);
    const [salesVisitSummary, setSalesVisitSummary] = useState([]);
    const [salesCustomerSummary, setSalesCustomerSummary] = useState([]);
    const [salesQuotationSummary, setSalesQuotationSummary] = useState([]);
    const [salesQuotationDetails, setSalesQuotationDetails] = useState([]);
    const [showQuotationModal, setShowQuotationModal] = useState(false);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [selectedQuotationList, setSelectedQuotationList] = useState([]);
    const [selectedQuotationId, setSelectedQuotationId] = useState(null);
    const [quotationSearchText, setQuotationSearchText] = useState("");
    const [quotationModalLoading, setQuotationModalLoading] = useState(false);
    const [quotationModalError, setQuotationModalError] = useState("");
    const [quotationInvoiceData, setQuotationInvoiceData] = useState(null);
    const [quotationInstitution, setQuotationInstitution] = useState(null);
    const [visitWindowDays, setVisitWindowDays] = useState(7);
    const [selectedVisit, setSelectedVisit] = useState(null);

    useEffect(() => {
        const headers = authInfo?.token
            ? {
                  "auth-token": authInfo.token,
                  Authorization: `Bearer ${authInfo.token}`,
              }
            : {};

        const fetchDashboardData = async (silent = false) => {
            if (!silent) {
                setLoading(true);
                setError("");
            }

            let nextError = "";

            try {
                const recentVisitResponse = await fetchRecentSalesVisitsData(headers, 200);
                setRecentVisits(extractRecentVisits(recentVisitResponse.data));
            } catch (err) {
                setRecentVisits([]);
                nextError = err?.message || "Failed to load recent sales visits.";
            }

            try {
                const customerResponse = await fetchSalesCustomersData(headers);
                setTotalCustomers(extractCustomerCount(customerResponse.data));
            } catch (_err) {
                setTotalCustomers(0);
                if (!nextError) nextError = "Failed to load customer count.";
            }

            try {
                const quotationResponse = await fetchQuotationsData(headers);
                setTotalQuotations(extractQuotationCount(quotationResponse.data));
            } catch (_err) {
                setTotalQuotations(0);
                if (!nextError) nextError = "Failed to load quotation count.";
            }

            try {
                const quotationSummaryResponse = await fetchSalesQuotationSummaryData(headers);
                const { summary, details } = extractSalesQuotationSummaryPayload(
                    quotationSummaryResponse.data
                );
                setSalesQuotationSummary(summary);
                setSalesQuotationDetails(details);
            } catch (err) {
                setSalesQuotationSummary([]);
                setSalesQuotationDetails([]);
                if (!nextError) {
                    nextError = err?.message || "Failed to load sales quotation summary.";
                }
            }

            try {
                const summaryResponse = await fetchSalesVisitSummaryData(headers);
                setSalesVisitSummary(extractSummaryRows(summaryResponse.data));
            } catch (_err) {
                setSalesVisitSummary([]);
                if (!nextError) nextError = "Failed to load sales visit summary.";
            }

            try {
                const customerSummaryResponse = await fetchSalesCustomerSummaryData(headers);
                setSalesCustomerSummary(extractSummaryRows(customerSummaryResponse.data));
            } catch (_err) {
                setSalesCustomerSummary([]);
                if (!nextError) nextError = "Failed to load sales customer summary.";
            }

            if (nextError && !silent) setError(nextError);
            if (!silent) setLoading(false);
        };

        fetchDashboardData(false);
        const intervalId = setInterval(() => fetchDashboardData(true), 30000);
        return () => clearInterval(intervalId);
    }, [authInfo?.token]);

    const summaryAggregates = useMemo(() => {
        return salesVisitSummary.reduce(
            (acc, row) => {
                acc.totalVisits += Number(row.total_visits || 0);
                acc.pendingFollowUps += Number(row.upcoming_followups || 0);
                acc.overdueFollowUps += Number(row.overdue_followups || 0);
                acc.priorityA += Number(row.priority_a_count || 0);
                return acc;
            },
            {
                totalVisits: 0,
                pendingFollowUps: 0,
                overdueFollowUps: 0,
                priorityA: 0,
            }
        );
    }, [salesVisitSummary]);

    const pendingFollowUps =
        salesVisitSummary.length > 0
            ? summaryAggregates.pendingFollowUps
            : 0;
    const totalUpcomingFollowUps = pendingFollowUps;
    const overdueFollowUps =
        salesVisitSummary.length > 0
            ? summaryAggregates.overdueFollowUps
            : 0;
    const priorityACount =
        salesVisitSummary.length > 0
            ? summaryAggregates.priorityA
            : 0;
    const totalVisits =
        salesVisitSummary.length > 0
            ? summaryAggregates.totalVisits
            : 0;

    const customerCountBySalesPersonId = useMemo(() => {
        const map = {};
        salesCustomerSummary.forEach((row) => {
            const id = String(row.sales_person_id ?? row.assigned_sales_person_id ?? "");
            if (!id) return;
            map[id] = Number(row.total_customers || 0);
        });
        return map;
    }, [salesCustomerSummary]);

    const mergedSummaryRows = useMemo(() => {
        const map = new Map();

        salesVisitSummary.forEach((row) => {
            const id = String(row.sales_person_id ?? "");
            if (!id) return;
            map.set(id, {
                sales_person_id: row.sales_person_id,
                sales_person_area: row.sales_person_area,
                sales_person_name: row.sales_person_name || "-",
                total_customers: Number(customerCountBySalesPersonId[id] || 0),
                total_visits: Number(row.total_visits || 0),
                upcoming_followups: Number(row.upcoming_followups || 0),
                overdue_followups: Number(row.overdue_followups || 0),
                completed_count: Number(row.completed_count || 0),
                reschedule_count: Number(row.reschedule_count || 0),
                priority_a_count: Number(row.priority_a_count || 0),
            });
        });

        salesCustomerSummary.forEach((row) => {
            const id = String(row.sales_person_id ?? row.assigned_sales_person_id ?? "");
            if (!id) return;

            if (!map.has(id)) {
                map.set(id, {
                    sales_person_id: Number(id),
                    sales_person_name: row.sales_person_name || "Unassigned",
                    sales_person_area: row.sales_person_area,
                    total_customers: Number(row.total_customers || 0),
                    total_visits: 0,
                    upcoming_followups: 0,
                    overdue_followups: 0,
                    completed_count: 0,
                    reschedule_count: 0,
                    priority_a_count: 0,
                });
            } else {
                const existing = map.get(id);
                existing.total_customers = Number(row.total_customers || 0);
                if (!existing.sales_person_area && row.sales_person_area) {
                    existing.sales_person_area = row.sales_person_area;
                }
                map.set(id, existing);
            }
        });

        return Array.from(map.values()).sort((a, b) => {
            if (b.total_customers !== a.total_customers) {
                return b.total_customers - a.total_customers;
            }
            return (a.sales_person_name || "").localeCompare(b.sales_person_name || "");
        });
    }, [salesVisitSummary, salesCustomerSummary, customerCountBySalesPersonId]);

    const salesPersonVisitCounts = useMemo(() => {
        if (salesVisitSummary.length > 0) {
            return salesVisitSummary
                .map((row) => ({
                    name: row.sales_person_name || "-",
                    count: Number(row.total_visits || 0),
                }))
                .sort((a, b) => b.count - a.count);
        }

        const recentSix = [...recentVisits]
            .sort((a, b) => {
                const aDate = new Date(a.visit_date || "1970-01-01").getTime();
                const bDate = new Date(b.visit_date || "1970-01-01").getTime();
                return bDate - aDate;
            })
            .slice(0, 6);

        if (recentSix.length > 0) {
            const counts = recentSix.reduce((acc, item) => {
                const salesPersonName = (
                    item.sales_person_name ||
                    item.sales_person ||
                    item.user_full_name ||
                    item.full_name ||
                    "Unknown"
                )
                    .toString()
                    .trim();

                acc[salesPersonName] = (acc[salesPersonName] || 0) + 1;
                return acc;
            }, {});

            return Object.entries(counts)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count);
        }

        return [];
    }, [recentVisits, salesVisitSummary]);

    const filteredRecentVisits = useMemo(() => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        const cutoff = new Date(today);
        cutoff.setDate(cutoff.getDate() - (Number(visitWindowDays) - 1));
        cutoff.setHours(0, 0, 0, 0);

        return [...recentVisits]
            .filter((item) => {
                const dt = new Date(item?.visit_date || "");
                return !Number.isNaN(dt.getTime()) && dt >= cutoff && dt <= today;
            })
            .sort((a, b) => {
                const aDate = new Date(a.visit_date || "1970-01-01").getTime();
                const bDate = new Date(b.visit_date || "1970-01-01").getTime();
                return bDate - aDate;
            });
    }, [recentVisits, visitWindowDays]);

    const todayDateOnly = useMemo(() => formatLocalDateKey(new Date()), []);
    const tomorrowDateOnly = useMemo(() => {
        const dt = new Date();
        dt.setDate(dt.getDate() + 1);
        return formatLocalDateKey(dt);
    }, []);

    const todayFollowups = useMemo(() => {
        return recentVisits
            .filter((item) => toDateOnly(item.next_followup_date) === todayDateOnly)
            .sort((a, b) => {
                const aDate = new Date(a.next_followup_date || a.visit_date || "1970-01-01").getTime();
                const bDate = new Date(b.next_followup_date || b.visit_date || "1970-01-01").getTime();
                return aDate - bDate;
            });
    }, [recentVisits, todayDateOnly]);

    const tomorrowFollowups = useMemo(() => {
        return recentVisits
            .filter((item) => toDateOnly(item.next_followup_date) === tomorrowDateOnly)
            .sort((a, b) => {
                const aDate = new Date(a.next_followup_date || a.visit_date || "1970-01-01").getTime();
                const bDate = new Date(b.next_followup_date || b.visit_date || "1970-01-01").getTime();
                return aDate - bDate;
            });
    }, [recentVisits, tomorrowDateOnly]);

    const quotationDetailsBySalesPersonId = useMemo(() => {
        const map = {};

        salesQuotationDetails.forEach((row) => {
            const id = String(row.sales_person_id ?? row.sale_created_by ?? "");
            if (!id) return;
            if (!map[id]) map[id] = [];
            map[id].push(row);
        });

        Object.keys(map).forEach((id) => {
            map[id].sort((a, b) => Number(b.sale_id || 0) - Number(a.sale_id || 0));
        });

        return map;
    }, [salesQuotationDetails]);

    const filteredOtherQuotations = useMemo(() => {
        const keyword = quotationSearchText.trim().toLowerCase();
        if (!keyword) return selectedQuotationList;

        return selectedQuotationList.filter((q) => {
            const fields = [
                q.sale_invoice,
                q.sale_invoice_no,
                q.sale_id,
                q.sale_total_amount,
                q.sale_created_isodt,
            ]
                .map((v) => String(v || "").toLowerCase())
                .join(" ");
            return fields.includes(keyword);
        });
    }, [selectedQuotationList, quotationSearchText]);

    const fetchQuotationPreview = async (saleId, preloadedQuotation = null) => {
        if (!saleId) return;

        const headers = authInfo?.token
            ? {
                  "auth-token": authInfo.token,
                  Authorization: `Bearer ${authInfo.token}`,
              }
            : {};

        setQuotationModalLoading(true);
        setQuotationModalError("");

        try {
            // Use the same working endpoint as quotation-invoice page
            const [institutionRes, quotationDetailRes] = await Promise.all([
                fetchInstitution(headers).catch(() => ({ data: null })),
                fetchQuotationWithDetails(headers, saleId),
            ]);

            setQuotationInstitution(institutionRes?.data || null);
            const fromInvoice = extractSingleQuotation(quotationDetailRes.data);

            if (!fromInvoice) {
                throw new Error("No quotation details returned from invoice endpoint.");
            }

            setQuotationInvoiceData(normalizeQuotationPreview(fromInvoice, preloadedQuotation));
        } catch (err) {
            // Last fallback: keep at least preloaded summary row visible
            const fallbackRow =
                preloadedQuotation ||
                salesQuotationDetails.find((row) => Number(row.sale_id) === Number(saleId)) ||
                null;
            setQuotationInvoiceData(fallbackRow ? normalizeQuotationPreview(fallbackRow) : null);
            if (fallbackRow) {
                setQuotationModalError("");
            } else {
                setQuotationModalError(err?.message || "Failed to load quotation details.");
            }
        } finally {
            setQuotationModalLoading(false);
        }
    };

    const openQuotationModal = (details) => {
        if (!details || details.length === 0) return;
        const sorted = [...details].sort((a, b) => Number(b.sale_id || 0) - Number(a.sale_id || 0));
        const first = sorted[0];
        setQuotationSearchText("");
        setSelectedQuotationList(sorted);
        setSelectedQuotation(first);
        setSelectedQuotationId(first?.sale_id || null);
        setQuotationInvoiceData(first || null);
        setQuotationModalError("");
        setQuotationModalLoading(false);
        setShowQuotationModal(true);
        fetchQuotationPreview(first?.sale_id, first);
    };

    const switchQuotationInModal = (quotation) => {
        setSelectedQuotation(quotation);
        setSelectedQuotationId(quotation?.sale_id || null);
        setQuotationInvoiceData(quotation || null);
        setQuotationModalError("");
        setQuotationModalLoading(false);
        fetchQuotationPreview(quotation?.sale_id, quotation);
    };

    return (
        <div className="dashboard">
            <CustomerHeader headerName="Dashboard" showActions={false} />

            {error && <div className="alert">{error}</div>}

            <div className="stats-grid">
                <StatCard title="Total Customers" value={totalCustomers} tone="customers" />
                <StatCard title="Total Quotations" value={totalQuotations} tone="quotation" />
                <StatCard title="Total Visits" value={totalVisits} tone="visits" />
                <StatCard title="Upcoming Follow-Ups" value={totalUpcomingFollowUps} tone="upcoming" />
                <StatCard title="Overdue Follow-Ups" value={overdueFollowUps} tone="overdue" />
                <StatCard title="Priority A Customers" value={priorityACount} tone="priority" />
            </div>

            <div className="content-grid">
                <div className="card recentVisitsCard">
                    <div className="recentVisitsHead">
                        <h3>Recent Sales Visits</h3>
                        <select
                            className="recentVisitsFilter"
                            value={visitWindowDays}
                            onChange={(e) => setVisitWindowDays(Number(e.target.value))}
                        >
                            <option value={7}>Last 7 Day</option>
                            <option value={30}>Last 30 Days</option>
                            <option value={90}>Last 90 Days</option>
                        </select>
                    </div>
                    {loading ? (
                        <p>Loading...</p>
                    ) : filteredRecentVisits.length === 0 ? (
                        <p>No visit data found.</p>
                    ) : (
                        <div className="recentVisitsTableWrap">
                            <table className="recentVisitsTable">
                                <thead>
                                <tr>
                                    <th>Customer Name ↑↓</th>
                                    <th>Salesperson</th>
                                    <th>Area</th>
                                    <th>Date ↑↓</th>
                                    <th>Status ↑↓</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecentVisits.map((item, idx) => (
                                    <tr key={`visit-${item.id || idx}`}>
                                        <td>{item.customer_name || "-"}</td>
                                        <td>{item.sales_person_name || item.sales_person || "-"}</td>
                                        <td>{item.sales_person_area || "-"}</td>
                                        <td>{formatDate(item.visit_date)}</td>
                                            <td>
                                                <VisitStatusBadge status={item.status} />
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="visitDetailsBtn"
                                                    onClick={() => setSelectedVisit(item)}
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="card">
                    <h3>Visits by Sales Person</h3>
                    {loading ? (
                        <p>Loading...</p>
                    ) : salesPersonVisitCounts.length === 0 ? (
                        <p>No visit data found.</p>
                    ) : (
                        salesPersonVisitCounts.map((item) => (
                            <div className="list-item" key={`sales-person-${item.name}`}>
                                <div>
                                    <strong>{item.name}</strong>
                                    <p>Total Visits</p>
                                </div>
                                <span className="priority pC">{item.count}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="card" style={{ marginTop: "16px" }}>
                <h3>Upcoming Follow-ups</h3>
                <div className="upcomingSummary">
                    <span className="upBadge">Today: {todayFollowups.length}</span>
                    <span className="upBadge">Tomorrow: {tomorrowFollowups.length}</span>
                </div>

                <div className="upSectionTitle">Today ({todayFollowups.length})</div>
                {todayFollowups.length === 0 ? (
                    <p className="upEmpty">No follow-ups for today</p>
                ) : (
                    todayFollowups.map((item) => (
                        <div className="upItem" key={`today-${item.id}`}>
                            <strong>{item.customer_name || "-"}</strong>
                            <p>
                                {formatDate(item.next_followup_date)} - {formatSalespersonWithArea(item)}
                            </p>
                        </div>
                    ))
                )}

                <div className="upSectionTitle">Tomorrow ({tomorrowFollowups.length})</div>
                {tomorrowFollowups.length === 0 ? (
                    <p className="upEmpty">No follow-ups for tomorrow</p>
                ) : (
                    tomorrowFollowups.map((item) => (
                        <div className="upItem" key={`tomorrow-${item.id}`}>
                            <strong>{item.customer_name || "-"}</strong>
                            <p>
                                {formatDate(item.next_followup_date)} - {formatSalespersonWithArea(item)}
                            </p>
                        </div>
                    ))
                )}
            </div>

            

            <div className="card" style={{ marginTop: "16px" }}>
                <h3>Sales Visit Summary</h3>
                {loading ? (
                    <p>Loading...</p>
                ) : mergedSummaryRows.length === 0 ? (
                    <p>No summary data found.</p>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>ID</th>
                                    <th style={thStyle}>Sales Person</th>
                                    <th style={thStyle}>Area</th>
                                    <th style={thStyle}>Total Customers</th>
                                    <th style={thStyle}>Total Visits</th>
                                    <th style={thStyle}>Upcoming</th>
                                    <th style={thStyle}>Overdue</th>
                                    <th style={thStyle}>Completed</th>
                                    <th style={thStyle}>Reschedule</th>
                                    <th style={thStyle}>Priority A</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mergedSummaryRows.map((row) => (
                                    <tr key={`summary-${row.sales_person_id}`}>
                                        <td style={tdStyle}>{row.sales_person_id}</td>
                                        <td style={tdStyle}>{row.sales_person_name || "-"}</td>
                                        <td style={tdStyle}>{row.sales_person_area || "-"}</td>
                                        <td style={tdStyle}>{row.total_customers || 0}</td>
                                        <td style={tdStyle}>{row.total_visits || 0}</td>
                                        <td style={tdStyle}>{row.upcoming_followups || 0}</td>
                                        <td style={tdStyle}>{row.overdue_followups || 0}</td>
                                        <td style={tdStyle}>{row.completed_count || 0}</td>
                                        <td style={tdStyle}>{row.reschedule_count || 0}</td>
                                        <td style={tdStyle}>{row.priority_a_count || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="card" style={{ marginTop: "16px" }}>
                <h3>Sales Quotation Summary</h3>
                {loading ? (
                    <p>Loading...</p>
                ) : salesQuotationSummary.length === 0 ? (
                    <p>No quotation summary found.</p>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Sales Man Name</th>
                                    <th style={thStyle}>Quotation Number</th>
                                    <th style={thStyle}>Quotation Details</th>
                                    <th style={thStyle}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesQuotationSummary.map((row) => {
                                    const id = String(row.sales_person_id ?? "");
                                    const detailRows = quotationDetailsBySalesPersonId[id] || [];
                                    const top10 = detailRows.slice(0, 10);
                                    const latestQuotation = detailRows[0];

                                    return (
                                        <tr key={`quotation-summary-${id || row.sales_person_name}`}>
                                            <td style={tdStyle}>{row.sales_person_name || "-"}</td>
                                            <td style={tdStyle}>{Number(row.total_quotations || 0)}</td>
                                            <td style={tdStyle}>
                                                {detailRows.length === 0 ? (
                                                    "-"
                                                ) : (
                                                    <>
                                                        {top10.map((d) => (
                                                            <div
                                                                key={`q-detail-${id}-${d.sale_id}`}
                                                                style={{ marginBottom: "4px" }}
                                                            >
                                                                #{d.sale_id} | {formatDateTime(d.sale_created_isodt)}
                                                                {d.sale_total_amount != null
                                                                    ? ` | Amt: ${d.sale_total_amount}`
                                                                    : ""}
                                                                {d.sale_customer_id != null
                                                                    ? ` | Cus: ${d.sale_customer_id}`
                                                                    : ""}
                                                            </div>
                                                        ))}
                                                        {detailRows.length > 10 ? (
                                                            <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                                                +{detailRows.length - 10} more
                                                            </div>
                                                        ) : null}
                                                    </>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                {latestQuotation?.sale_id ? (
                                                    <button
                                                        type="button"
                                                        className="eyeActionBtn"
                                                        title="View quotation details"
                                                        aria-label="View quotation details"
                                                        onClick={() => openQuotationModal(detailRows)}
                                                    >
                                                        👁
                                                    </button>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {selectedVisit ? (
                <div className="visitDetailsOverlay" onClick={() => setSelectedVisit(null)}>
                    <div className="visitDetailsDialog" onClick={(e) => e.stopPropagation()}>
                        <div className="visitDetailsHead">
                            <h3>Visit Details</h3>
                            <button
                                type="button"
                                className="visitDetailsClose"
                                onClick={() => setSelectedVisit(null)}
                            >
                                ×
                            </button>
                        </div>

                        <div className="visitDetailsGrid">
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Customer</p>
                                <strong>{selectedVisit.customer_name || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Salesperson</p>
                                <strong>{extractSalespersonName(selectedVisit)}</strong>
                                <p className="visitDetailsLabel" style={{ marginTop: "6px" }}>
                                    Sales Person Area
                                </p>
                                <strong>
                                    {extractSalespersonArea(selectedVisit) || "-"}
                                </strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Visit Date</p>
                                <strong>{formatVisitDateTime(selectedVisit.visit_date)}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Status</p>
                                <strong>{formatStatusText(selectedVisit.status)}</strong>
                            </div>
                            <div className="visitDetailsItem visitDetailsLocation">
                                <p className="visitDetailsLabel">Location</p>
                                <strong>
                                    {selectedVisit.location ||
                                        selectedVisit.visit_location ||
                                        selectedVisit.customer_area ||
                                        selectedVisit.customer_address ||
                                        "-"}
                                </strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Customer Phone</p>
                                <strong>{selectedVisit.customer_phone || selectedVisit.customer_mobile_no || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Customer Type</p>
                                <strong>{selectedVisit.customer_type || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Last PO Reference</p>
                                <strong>{selectedVisit.last_po_reference || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Competitor</p>
                                <strong>{selectedVisit.competitor || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Product Category</p>
                                <strong>
                                    {selectedVisit.product_category_name ||
                                        selectedVisit.product_category ||
                                        selectedVisit.product_category_id ||
                                        "-"}
                                </strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Customer Priority</p>
                                <strong>{selectedVisit.customer_priority || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Visit Agenda</p>
                                <strong>{selectedVisit.visit_agenda || "-"}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Next Follow-up Date</p>
                                <strong>{formatDate(selectedVisit.next_followup_date)}</strong>
                            </div>
                            <div className="visitDetailsItem">
                                <p className="visitDetailsLabel">Assigned Salesperson ID</p>
                                <strong>{selectedVisit.assigned_sales_person_id || "-"}</strong>
                            </div>
                        </div>

                        <div className="visitDetailsSection">
                            <h4>Visit Notes</h4>
                            <div className="visitNotesBox">
                                {selectedVisit.visit_notes ||
                                    selectedVisit.previous_feedback ||
                                    selectedVisit.notes ||
                                    selectedVisit.followup_note ||
                                    selectedVisit.note ||
                                    "No notes added."}
                            </div>
                        </div>

                        <div className="visitDetailsSection">
                            <h4>Action Items</h4>
                            <ul className="visitActionList">
                                {extractVisitActionItems(selectedVisit).map((item, idx) => (
                                    <li key={`visit-action-${idx}`}>{item}</li>
                                ))}
                            </ul>
                        </div>

                        <div className="visitDetailsSection">
                            <h4>Meeting Persons</h4>
                            <ul className="visitActionList">
                                {extractMeetingPersons(selectedVisit).length > 0 ? (
                                    extractMeetingPersons(selectedVisit).map((person, idx) => {
                                        const view = formatMeetingPerson(person);
                                        return (
                                            <li key={`meeting-person-${idx}`}>
                                                <strong>{view.name}</strong>
                                                {view.details.length > 0 ? (
                                                    <div style={{ marginTop: "4px", fontSize: "14px", color: "#4b5563" }}>
                                                        {view.details.join(" | ")}
                                                    </div>
                                                ) : null}
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li>No meeting person added.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : null}

            {showQuotationModal ? (
                <div className="quotationModalOverlay" onClick={() => setShowQuotationModal(false)}>
                    <div className="quotationModalCard" onClick={(e) => e.stopPropagation()}>
                        <div className="quotationModalHead">
                            <h3>Quotation Details</h3>
                            <button
                                type="button"
                                className="quotationCloseBtn"
                                onClick={() => setShowQuotationModal(false)}
                            >
                                Close
                            </button>
                        </div>

                        {quotationModalLoading ? (
                            <p>Loading quotation details...</p>
                        ) : quotationModalError ? (
                            <p>{quotationModalError}</p>
                        ) : quotationInvoiceData ? (
                            <>
                                {selectedQuotationList.length > 1 ? (
                                    <>
                                        <h4 style={{ margin: "8px 0 10px" }}>Other Quotations</h4>
                                        <div className="quotationSearchInputWrap">
                                            <input
                                                type="text"
                                                className="quotationSearchInput"
                                                placeholder="Search by invoice, id, date, amount..."
                                                value={quotationSearchText}
                                                onChange={(e) => setQuotationSearchText(e.target.value)}
                                            />
                                            <button type="button" className="quotationSearchBtn">
                                                Search
                                            </button>
                                        </div>
                                        <div className="quotationListWrap">
                                            {filteredOtherQuotations.map((q) => (
                                                <button
                                                    key={`q-modal-list-${q.sale_id}`}
                                                    type="button"
                                                    className={`quotationChip ${
                                                        selectedQuotationId === q.sale_id ? "active" : ""
                                                    }`}
                                                    onClick={() => switchQuotationInModal(q)}
                                                >
                                                    {q.sale_invoice || q.sale_invoice_no || `Q-${q.sale_id}`}
                                                </button>
                                            ))}
                                            {filteredOtherQuotations.length === 0 ? (
                                                <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>
                                                    No matching quotation found.
                                                </p>
                                            ) : null}
                                        </div>
                                    </>
                                ) : null}

                                <div className="quotationHeaderGrid">
                                    <div className="quotationHeaderCol">
                                        <div><strong>Customer ID :</strong> {quotationInvoiceData.customer_code || quotationInvoiceData.sale_customer_id || "-"}</div>
                                        <div><strong>Customer Name :</strong> {quotationInvoiceData.customer_name || "-"}</div>
                                        <div><strong>Institution Name :</strong> {quotationInvoiceData.customer_institution_name || quotationInstitution?.pro_name || "-"}</div>
                                        <div><strong>Customer Address :</strong> {quotationInvoiceData.customer_address || "-"}</div>
                                        <div><strong>Customer Mobile :</strong> {quotationInvoiceData.customer_mobile_no || "-"}</div>
                                    </div>
                                    <div className="quotationHeaderCol">
                                        <div><strong>Quotation By :</strong> {quotationInvoiceData.employee_name || quotationInvoiceData.sales_person_name || "-"}</div>
                                        <div><strong>Invoice No :</strong> {quotationInvoiceData.sale_invoice || quotationInvoiceData.sale_invoice_no || `Q-${quotationInvoiceData.sale_id}`}</div>
                                        <div><strong>Date &amp; Time :</strong> {formatDateTime(quotationInvoiceData.sale_created_isodt)}</div>
                                    </div>
                                </div>

                                <div className="quotationTableWrap">
                                    <table className="quotationPreviewTable">
                                        <thead>
                                            <tr>
                                                <th>SL</th>
                                                <th>Category</th>
                                                <th>Product</th>
                                                <th>Brand</th>
                                                <th>Unit</th>
                                                <th>Unit Price in BDT</th>
                                                <th>QTY</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {extractQuotationLines(quotationInvoiceData).map((line, idx) => {
                                                const unitPrice = resolveLineUnitPrice(line);
                                                const qty = resolveLineQty(line);
                                                const total = resolveLineTotal(line, unitPrice, qty);
                                                return (
                                                    <tr key={`q-line-${idx}`}>
                                                        <td>{idx + 1}</td>
                                                        <td>{line.prod_cat_name || line.category_name || line.sale_cat_name || "-"}</td>
                                                        <td>{line.prod_name || line.product_name || line.entered_set_no || line.sale_product_name || "-"}</td>
                                                        <td>{line.prod_brand_name || line.brand_name || line.sale_brand_name || "-"}</td>
                                                        <td>{line.prod_unit_name || line.unit_name || line.sale_unit_name || line.sale_unit || "-"}</td>
                                                        <td>{unitPrice ?? "-"}</td>
                                                        <td>{qty ?? "-"}</td>
                                                        <td>{total ?? "-"}</td>
                                                    </tr>
                                                );
                                            })}
                                            {extractQuotationLines(quotationInvoiceData).length === 0 ? (
                                                <tr>
                                                    <td colSpan={8}>No item lines found</td>
                                                </tr>
                                            ) : null}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="quotationFooterInfo">
                                    <p>
                                        In Word : {numberToWords(quotationInvoiceData.sale_total_amount)} only
                                    </p>
                                    <p>Note : {quotationInvoiceData.sale_note || "N/A"}</p>
                                    <strong>Terms &amp; Conditions:</strong>
                                    <ul>
                                        {extractTerms(quotationInvoiceData).map((term, idx) => (
                                            <li key={`term-${idx}`}>{term}</li>
                                        ))}
                                        {extractTerms(quotationInvoiceData).length === 0 ? (
                                            <li>N/A</li>
                                        ) : null}
                                    </ul>
                                </div>

                            </>
                        ) : (
                            <p>No quotation detail found.</p>
                        )}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function extractRecentVisits(payload) {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.message)
            ? payload.message
            : Array.isArray(payload?.result)
              ? payload.result
              : [];

    // Keep only rows that look like visit report records.
    return rows.filter((row) => row && (row.visit_date || row.customer_name));
}

function extractCustomerCount(payload) {
    if (typeof payload?.count === "number") return payload.count;
    if (Array.isArray(payload?.data)) return payload.data.length;
    if (Array.isArray(payload)) return payload.length;
    return 0;
}

function extractQuotationCount(payload) {
    if (typeof payload?.total === "number") return payload.total;
    if (Array.isArray(payload?.message)) return payload.message.length;
    if (Array.isArray(payload?.data)) return payload.data.length;
    if (Array.isArray(payload)) return payload.length;
    return 0;
}

function extractSummaryRows(payload) {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
}

function extractSalesQuotationSummaryPayload(payload) {
    const summary = Array.isArray(payload?.summary)
        ? payload.summary
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
    const details = Array.isArray(payload?.details) ? payload.details : [];
    return { summary, details };
}

function extractArrayRows(payload) {
    if (Array.isArray(payload?.message)) return payload.message;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
}

function extractSingleQuotation(payload) {
    if (Array.isArray(payload?.message) && payload.message.length > 0) return payload.message[0];
    if (Array.isArray(payload?.data) && payload.data.length > 0) return payload.data[0];
    if (payload?.message && typeof payload.message === "object" && !Array.isArray(payload.message)) {
        return payload.message;
    }
    if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
        return payload.data;
    }
    return null;
}

function normalizeQuotationPreview(source, base = null) {
    const row = source || {};
    const root = {
        ...(base || {}),
        ...row,
    };

    const lineItems = Array.isArray(row.line_items)
        ? row.line_items
        : Array.isArray(row.details)
          ? row.details
          : Array.isArray(row.sale_details)
            ? row.sale_details
            : [];

    return {
        ...root,
        customer_code: root.customer_code || root.customer_id || root.sale_customer_id || "-",
        customer_name: root.customer_name || root.cus_name || "-",
        customer_address: root.customer_address || root.cus_address || "-",
        customer_mobile_no: root.customer_mobile_no || root.cus_mobile || "-",
        sale_invoice: root.sale_invoice || root.sale_invoice_no || (root.sale_id ? `Q-${root.sale_id}` : "-"),
        details: lineItems,
        line_items: lineItems,
    };
}

function extractQuotationLines(quotation) {
    if (Array.isArray(quotation?.line_items)) return quotation.line_items;
    if (Array.isArray(quotation?.details)) return quotation.details;
    if (Array.isArray(quotation?.sale_details)) return quotation.sale_details;
    if (Array.isArray(quotation?.items)) return quotation.items;
    return [];
}

function toNumberOrNull(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function resolveLineUnitPrice(line) {
    return (
        toNumberOrNull(line?.sale_rate) ??
        toNumberOrNull(line?.unit_price) ??
        toNumberOrNull(line?.rate) ??
        toNumberOrNull(line?.sale_unit_price)
    );
}

function resolveLineQty(line) {
    return (
        toNumberOrNull(line?.sale_qty) ??
        toNumberOrNull(line?.qty) ??
        toNumberOrNull(line?.quantity) ??
        toNumberOrNull(line?.sale_quantity) ??
        toNumberOrNull(line?.prod_qty) ??
        toNumberOrNull(line?.returned_quantity)
    );
}

function resolveLineTotal(line, unitPrice, qty) {
    return (
        toNumberOrNull(line?.sale_amount) ??
        toNumberOrNull(line?.line_total) ??
        toNumberOrNull(line?.total_amount) ??
        toNumberOrNull(line?.sale_total_amount) ??
        toNumberOrNull(line?.returned_amount) ??
        (unitPrice != null && qty != null ? Number((unitPrice * qty).toFixed(2)) : null)
    );
}

function extractTerms(quotation) {
    const raw = quotation?.checkbox_options;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
        return [];
    }
}

async function fetchRecentSalesVisitsData(headers, limit = 6) {
    let lastError = null;

    for (const url of RECENT_SALES_VISIT_APIS) {
        try {
            return await axios.get(url, { headers, params: { include_all: 1, limit } });
        } catch (errGet) {
            lastError = errGet;
        }

        try {
            return await axios.post(url, { include_all: 1, limit }, { headers });
        } catch (errPost) {
            lastError = errPost;
        }
    }

    const serverMessage =
        lastError?.response?.data?.message ||
        lastError?.response?.data?.error ||
        lastError?.message ||
        "Failed to load recent sales visits.";

    throw new Error(serverMessage);
}

async function fetchSalesCustomersData(headers) {
    let lastError = null;

    for (const url of SALES_CUSTOMER_APIS) {
        try {
            return await axios.get(url, { headers });
        } catch (err) {
            lastError = err;
        }
    }

    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load customer count."
    );
}

async function fetchSalesVisitSummaryData(headers) {
    let lastError = null;

    for (const url of SALES_VISIT_SUMMARY_APIS) {
        try {
            return await axios.get(url, { headers });
        } catch (err) {
            lastError = err;
        }
    }

    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load sales visit summary."
    );
}

async function fetchSalesCustomerSummaryData(headers) {
    let lastError = null;

    for (const url of SALES_CUSTOMER_SUMMARY_APIS) {
        try {
            return await axios.get(url, { headers });
        } catch (err) {
            lastError = err;
        }
    }

    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load sales customer summary."
    );
}

async function fetchQuotationsData(headers) {
    let lastError = null;

    for (const url of QUOTATION_APIS) {
        try {
            return await axios.post(url, { reqPayload: {} }, { headers });
        } catch (err) {
            lastError = err;
        }
    }

    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load quotation count."
    );
}

async function fetchSalesQuotationSummaryData(headers, params = {}) {
    let lastError = null;

    for (const url of SALES_QUOTATION_SUMMARY_APIS) {
        try {
            return await axios.get(url, { headers, params });
        } catch (err) {
            lastError = err;
        }

        try {
            return await axios.post(url, params, { headers });
        } catch (err) {
            lastError = err;
        }
    }

    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load sales quotation summary."
    );
}

async function fetchInstitution(headers) {
    return axios.get(`${API_URL}/api/get-institution`, { headers });
}

async function fetchQuotationWithDetails(headers, saleId) {
    let lastError = null;
    const urls = [
        `${API_URL}/api/get-quotation-with-details`,
        `${API_URL}/get-quotation-with-details`,
    ];
    for (const url of urls) {
        try {
            return await axios.post(
                url,
                { reqPayload: { saleId: Number(saleId), from: "invoice" } },
                { headers }
            );
        } catch (err) {
            lastError = err;
        }
    }
    throw new Error(
        lastError?.response?.data?.message ||
            lastError?.response?.data?.error ||
            lastError?.message ||
            "Failed to load quotation with details."
    );
}

const thStyle = {
    textAlign: "left",
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "13px",
    color: "#374151",
    whiteSpace: "nowrap",
};

const tdStyle = {
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "14px",
    color: "#111827",
    whiteSpace: "nowrap",
};

function StatCard({ title, value, tone }) {
    const toneMeta = {
        customers: {
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M16 11a3 3 0 1 0-2.999-3A3 3 0 0 0 16 11m-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3m0 2c-2.67 0-8 1.34-8 4v1h10v-1c0-1.34.52-2.47 1.49-3.33A11.2 11.2 0 0 0 8 13m8 0c-.29 0-.62.02-.97.05A5.04 5.04 0 0 1 18 17v1h6v-1c0-2.66-5.33-4-8-4" />
                </svg>
            ),
        },
        quotation: {
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM8 13h8v2H8zm0-4h8v2H8zm5-6.5L18.5 8H13z" />
                </svg>
            ),
        },
        visits: {
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m16 6 2.29 2.29-4.88 4.88-3-3L5 15.59 6.41 17l4-4 3 3 6.3-6.29L22 12V6z" />
                </svg>
            ),
        },
        upcoming: {
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 16H5V10h14zm0-12H5V6h14z" />
                </svg>
            ),
        },
        overdue: {
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M11 15h2v2h-2zm0-8h2v6h-2zm1-5a10 10 0 1 0 10 10A10 10 0 0 0 12 2m0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8" />
                </svg>
            ),
        },
        priority: {
            icon: (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="m12 17.27 4.15 2.51-1.1-4.72L18.7 11.9l-4.81-.41L12 7l-1.89 4.49-4.81.41 3.65 3.16-1.1 4.72zM12 4.15 13.09 7l2.91.25L13.8 9.5 14.46 12 12 10.56 9.54 12l.66-2.5L8 7.25 10.91 7z" />
                </svg>
            ),
        },
    };
    const meta = toneMeta[tone] || { icon: null };

    return (
        <div className={`stat-card ${tone ? `stat-${tone}` : ""}`}>
            <span className="stat-card-icon">{meta.icon}</span>
            <p className="title">{title}</p>
            <h3>{value}</h3>
            <p className="stat-card-subtitle">Click to view details</p>
        </div>
    );
}

function VisitStatusBadge({ status }) {
    const text = formatStatusText(status);
    const toneClass =
        text === "Completed"
            ? "rv-status-completed"
            : text === "Rescheduled"
              ? "rv-status-rescheduled"
              : "rv-status-pending";

    return (
        <span className={`rv-status ${toneClass}`}>
            <span className="rv-status-dot" />
            {text}
        </span>
    );
}

function formatDate(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString("en-GB");
}

function formatDateTime(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    return parsed.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
}

function formatVisitDateTime(date) {
    if (!date) return "-";
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return String(date);
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    const seconds = String(parsed.getSeconds()).padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
}

function formatStatusText(status) {
    const clean = String(status || "Pending").trim().toLowerCase();
    if (clean.includes("complete")) return "Completed";
    if (clean.includes("reschedule")) return "Rescheduled";
    return "Pending";
}

function formatSalespersonWithArea(item) {
    if (!item) return "-";
    const rawName =
        item.sales_person_name ||
        item.sales_person ||
        item.user_full_name ||
        item.full_name ||
        "-";
    const name = typeof rawName === "string" ? rawName.trim() : String(rawName).trim();

    const rawArea =
        item.sales_person_area || item.area || item.salesPersonArea || "";
    const area = typeof rawArea === "string" ? rawArea.trim() : rawArea
        ? String(rawArea).trim()
        : "";

    return area ? `${name} • ${area}` : name || "-";
}

function extractSalespersonName(item) {
    if (!item) return "-";
    const rawName =
        item.sales_person_name ||
        item.sales_person ||
        item.user_full_name ||
        item.full_name ||
        "-";
    return typeof rawName === "string" ? rawName.trim() || "-" : String(rawName).trim() || "-";
}

function extractSalespersonArea(item) {
    if (!item) return "";
    const rawArea = item.sales_person_area || item.area || item.salesPersonArea || "";
    return typeof rawArea === "string"
        ? rawArea.trim()
        : rawArea
          ? String(rawArea).trim()
          : "";
}

function extractVisitActionItems(visit) {
    if (!visit || typeof visit !== "object") {
        return ["No action item added."];
    }

    const candidates = [
        visit.action_items,
        visit.action_item,
        visit.follow_up_action,
        visit.followup_action,
        visit.follow_up_plan,
        visit.next_action_plan,
        visit.next_action,
    ];

    for (const raw of candidates) {
        if (Array.isArray(raw)) {
            const normalized = raw.map((item) => String(item || "").trim()).filter(Boolean);
            if (normalized.length > 0) return normalized;
        }

        if (typeof raw === "string" && raw.trim()) {
            const normalized = raw
                .split(/\r?\n|,|;/)
                .map((item) => item.trim())
                .filter(Boolean);
            if (normalized.length > 0) return normalized;
        }
    }

    return ["No action item added."];
}

function extractMeetingPersons(visit) {
    const raw = visit?.meeting_persons_details;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return [raw];
    if (typeof raw === "string" && raw.trim()) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
            if (parsed && typeof parsed === "object") return [parsed];
        } catch (_e) {
            return [];
        }
    }
    return [];
}

function formatMeetingPerson(person) {
    if (person == null) return { name: "-", details: [] };
    if (typeof person === "string") return { name: person, details: [] };

    const name =
        person.meeting_person_name ||
        person.meeting_name ||
        person.person_name ||
        person.name ||
        "-";

    const details = [];
    const usedKeys = new Set([
        "meeting_person_name",
        "meeting_name",
        "person_name",
        "name",
    ]);

    const knownFields = [
        { label: "Designation", keys: ["meeting_designation", "designation", "meeting_person_designation"] },
        { label: "Department", keys: ["meeting_department", "department"] },
        { label: "Mobile", keys: ["meeting_mobile", "mobile", "phone", "contact_no"] },
        { label: "Email", keys: ["meeting_email", "email", "mail"] },
        { label: "Company", keys: ["meeting_company", "company", "organization"] },
        { label: "Address", keys: ["meeting_address", "address"] },
    ];

    knownFields.forEach((field) => {
        const value = pickFirstNonEmpty(person, field.keys);
        if (value != null) {
            details.push(`${field.label}: ${value}`);
            field.keys.forEach((k) => usedKeys.add(k));
        }
    });

    Object.entries(person).forEach(([key, value]) => {
        if (usedKeys.has(key)) return;
        if (value == null) return;
        if (typeof value === "object") return;
        const text = String(value).trim();
        if (!text) return;
        details.push(`${toTitleLabel(key)}: ${text}`);
    });

    return { name: String(name), details };
}

function pickFirstNonEmpty(obj, keys) {
    for (const key of keys) {
        const value = obj?.[key];
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
}

function toTitleLabel(key) {
    return String(key || "")
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function toDateOnly(date) {
    return formatLocalDateKey(date);
}

function formatLocalDateKey(date) {
    if (!date) return null;
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return null;
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function numberToWords(value) {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount)) return "Zero";
    if (amount === 0) return "Zero";

    const ones = [
        "",
        "One",
        "Two",
        "Three",
        "Four",
        "Five",
        "Six",
        "Seven",
        "Eight",
        "Nine",
        "Ten",
        "Eleven",
        "Twelve",
        "Thirteen",
        "Fourteen",
        "Fifteen",
        "Sixteen",
        "Seventeen",
        "Eighteen",
        "Nineteen",
    ];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const toWordsUnder1000 = (n) => {
        let out = "";
        if (n >= 100) {
            out += `${ones[Math.floor(n / 100)]} Hundred `;
            n %= 100;
        }
        if (n >= 20) {
            out += `${tens[Math.floor(n / 10)]} `;
            n %= 10;
        }
        if (n > 0) out += `${ones[n]} `;
        return out.trim();
    };

    let n = Math.floor(amount);
    const parts = [];
    if (n >= 10000000) {
        parts.push(`${toWordsUnder1000(Math.floor(n / 10000000))} Crore`);
        n %= 10000000;
    }
    if (n >= 100000) {
        parts.push(`${toWordsUnder1000(Math.floor(n / 100000))} Lakh`);
        n %= 100000;
    }
    if (n >= 1000) {
        parts.push(`${toWordsUnder1000(Math.floor(n / 1000))} Thousand`);
        n %= 1000;
    }
    if (n > 0) parts.push(toWordsUnder1000(n));

    return parts.join(" ").replace(/\s+/g, " ").trim();
}

const mapStateToProps = (state) => ({
    authInfo: state.authInfoReducer,
});

export default connect(mapStateToProps)(CrmDashboard);
